import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { StructureSet } from '~/domain/patient/structureset';
import { sessionDataAtom } from '~/state/atoms/sessionAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import useSelectedPatient from '../patient/useSelectedPatient';

type AvailableStructureSet = {
    sopInstanceUID: string;
    label: string;
    isLoaded: boolean;
    structureSetObject?: StructureSet; // Only present if loaded
};

export default function useAvailableStructureSets(showSelected = false) {
    const patient = useSelectedPatient();
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const sessionData = useRecoilValue(sessionDataAtom);
    const [availableStructureSets, setAvailableStructureSets] = useState<
        AvailableStructureSet[]
    >([]);

    useEffect(() => {
        const combinedSets: AvailableStructureSet[] = [];

        // Add loaded structure sets (from patient)
        const loadedSets = patient
            ? Array.from(patient.structureSets)
                  .filter(
                      (structureSet) =>
                          showSelected ||
                          structureSet.dicomUid !==
                              currentStructureSet?.dicomUid
                  )
                  .map((structureSet) => ({
                      sopInstanceUID: structureSet.dicomUid,
                      label: structureSet.structureSetLabel,
                      isLoaded: true,
                      structureSetObject: structureSet,
                  }))
            : [];
        combinedSets.push(...loadedSets);

        // Add session structure sets (not loaded)
        if (sessionData?.payload.structure_sets) {
            const sessionSets = Array.from([
                ...sessionData.payload.structure_sets,
                ...(sessionData.payload.context_structure_set
                    ? [sessionData.payload.context_structure_set]
                    : []),
            ])
                .filter(
                    (structureSet) =>
                        !loadedSets.some(
                            (loadedSet) =>
                                loadedSet.sopInstanceUID ===
                                structureSet.sop_instance_uid
                        )
                )
                .map((structureSet) => ({
                    sopInstanceUID: structureSet.sop_instance_uid!,
                    label: structureSet.display_name ?? '',
                    isLoaded: false,
                }));
            combinedSets.push(...sessionSets);
        }

        setAvailableStructureSets(combinedSets);
    }, [patient, currentStructureSet, sessionData, showSelected]);

    return availableStructureSets;
}
